import { BackNavigationButton } from "$components/button/back-button";
import { ErrorBoundary } from "$components/error-boundary";
import { ErrorState } from "$components/error-state";
import { LoadingPage } from "$components/loading/loading-page";
import { FeatureFlagGuard } from "$global-providers/feature-flags-provider/feature-flag-guard";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { lazyLoad } from "$routes/_lib/lazy-load";
import { linkToAdminControlPanel } from "$routes/admin/links";
import { useQuery } from "@apollo/client";
import { defineMessages } from "react-intl";
import { Route, Routes, useLocation, useParams } from "react-router-dom";
import { ShepherdTour } from "react-shepherd";
import { FETCH_USER_DETAILS } from "./_lib/components/queries";
import { tourOptions } from "./_lib/tour/tour-options";
import { tourSteps } from "./_lib/tour/tour-steps";
import { linkToSkuManagement } from "./links";
import "./_lib/tour/style.css";

const messages = defineMessages({
  backToSkuManagement: {
    id: "skuManagement.navigation.backToSkuManagement",
    defaultMessage: "Back to SKU Management",
  },
  backToControlPanel: {
    id: "skuManagement.navigation.backToControlPanel",
    defaultMessage: "Back to Control Panel",
  },
});

function useGetUrlParams() {
  return new URLSearchParams(useLocation().search);
}

const AddSkuPage = lazyLoad(() =>
  import(/* webpackChunkName: "SkuManagement" */ "./chunk").then((module) => ({
    default: module.default.AddSkuPage,
  })),
);

const SkuBulkOperationsPage = lazyLoad(() =>
  import(/* webpackChunkName: "SkuManagement" */ "./chunk").then((module) => ({
    default: module.default.SkuBulkOperationsPage,
  })),
);

const SkuManagementPage = lazyLoad(() =>
  import(/* webpackChunkName: "SkuManagement" */ "./chunk").then((module) => ({
    default: module.default.SkuManagementPage,
  })),
);

const SkuManagementPageWrapper = lazyLoad(() =>
  import(/* webpackChunkName: "SkuManagement" */ "./chunk").then((module) => ({
    default: module.default.SkuManagementPageWrapper,
  })),
);

const SkusPage = lazyLoad(() =>
  import(/* webpackChunkName: "SkuManagement" */ "./chunk").then((module) => ({
    default: module.default.SkusPage,
  })),
);

const SupplierBulkOperationsPage = lazyLoad(() =>
  import(/* webpackChunkName: "SkuManagement" */ "./chunk").then((module) => ({
    default: module.default.SupplierBulkOperationsPage,
  })),
);

export function SkuManagementRoutes() {
  const {
    loading: userLoading,
    error: userError,
    data: userData,
  } = useQuery(FETCH_USER_DETAILS);
  const facilityId = userData?.myUser?.clinic;
  const getUrlParams = useGetUrlParams();

  if (userLoading) return <LoadingPage />;
  if (userError) return <ErrorState error={userError} />;

  const SkuPageWithBreadcrumbs = () => {
    const params = useParams();
    return (
      <>
        <BackNavigationButton
          to={linkToSkuManagement()}
          label={messages.backToSkuManagement}
        />
        <SkusPage facilityId={facilityId} params={params} />
      </>
    );
  };

  const SkuManagementPageWithBreadcrumbs = () => {
    // VMP route → vmpId only. AMP route → vmpId + ampId. Non-pharma route →
    // nonPharmaId only. AddSkuPage discriminates by checking nonPharmaId first,
    // then ampId, falling back to vmpId.
    const params = useParams<{
      vmpId?: string;
      ampId?: string;
      nonPharmaId?: string;
    }>();
    return (
      <>
        <BackNavigationButton
          to={linkToSkuManagement()}
          label={messages.backToSkuManagement}
        />
        <AddSkuPage facilityId={facilityId} params={params} />
      </>
    );
  };

  return (
    <ErrorBoundary name="sku-management routes">
      <SkuManagementPageWrapper>
        <ShepherdTour steps={tourSteps} tourOptions={tourOptions}>
          <Routes>
            <Route
              index
              element={
                <>
                  <BackNavigationButton
                    to={linkToAdminControlPanel()}
                    label={messages.backToControlPanel}
                  />
                  <SkuManagementPage
                    urlSearchTermParam={getUrlParams.get("searchTerm")}
                    tourEnabled={Boolean(getUrlParams.get("tour"))}
                  />
                </>
              }
            />
            {/* I don't think this is allowed */}
            <Route path=":vmpId">
              <Route path="view" element={<SkuPageWithBreadcrumbs />} />
              <Route
                path="add-sku"
                element={<SkuManagementPageWithBreadcrumbs />}
              />
              <Route path=":ampId">
                <Route path="view" element={<SkuPageWithBreadcrumbs />} />
                <Route
                  path="add-sku"
                  element={<SkuManagementPageWithBreadcrumbs />}
                />
              </Route>
            </Route>
            <Route path="non-pharma/:nonPharmaId">
              <Route
                path="add-sku"
                element={
                  <FeatureFlagGuard
                    flagKey={featureFlags.clinical.nonPharma}
                    render={() => <SkuManagementPageWithBreadcrumbs />}
                  />
                }
              />
            </Route>
            <Route path="bulk">
              <Route path="sku" element={<SkuBulkOperationsPage />} />
              <Route path="supplier" element={<SupplierBulkOperationsPage />} />
            </Route>
          </Routes>
        </ShepherdTour>
      </SkuManagementPageWrapper>
    </ErrorBoundary>
  );
}
